import { combineReducers } from 'redux';
import WidgetsState, {
    WidgetInstancesState,
    WidgetsDataState,
} from './WidgetsState';
import { widgetSharedStateReducer } from './shared/reducer';
import { blotterWidgetsReducer } from 'widgets/blotter/store/reducer';
import { fillsGraphWidgetsReducer } from 'widgets/fillsGraph/store/reducer';
import { liquiditySummaryWidgetsReducer } from 'widgets/liquiditySummary/store/reducer';
import { portfolioReturnsWidgetsReducer } from 'widgets/portfolioReturns/store/reducer';
import { portfolioSummaryReducer } from 'widgets/portfolioSummary/store/instances/reducer';
import { intraDayProfileWidgetsReducer } from 'widgets/intraDayProfile/store/reducer';
import { strategyParametersWidgetsReducer } from 'widgets/strategyParams/store/reducer';
import { unknownWidgetsReducer } from '../unknown/store/reducer';
import { venueAnalysisWidgetsReducer } from 'widgets/venueAnalysis/store/reducer';
import { openSubmissionsWidgetsReducer } from 'widgets/openSubmissions/store/reducer';
import {
    expertiseMakeWidgetsReducer,
    expertiseNighthawkWidgetsReducer,
    expertiseDnaWidgetsReducer,
} from 'widgets/expertise/store/reducer';
import { portfolioPlWidgetsReducer } from 'widgets/portfolioPl/store/reducer';
import { tilesWidgetsReducer } from 'widgets/tiles/store/reducer';
import { volumeCapsuleReducer } from 'widgets/liquiditySummary/volumeCapsule/state/reducer';
import { portfolioSummaryDataReducer } from 'widgets/portfolioSummary/store/data/reducer';
import { widgetsSharedConfigReducer } from 'widgets/store/sharedConfiguration/reducer';
import { symbolTileWidgetInstancesReducer } from 'widgets/symbolTile/store/instances/reducer';
import { openSubmissionsCapsuleWidgetsReducer } from 'widgets/openSubmissions/capsule/store/reducer';
import { rsiTileReducer } from 'widgets/signalTiles/rsiTile/state';
import { pfeTileReducer } from 'widgets/signalTiles/pfeTile/state';
import { candlestickTileWidgetsReducer } from 'widgets/candlestickTile/store/reducer';
import { obvTileWidgetsReducer } from 'widgets/obvTile/store/reducer';
import { myPortfolioCapsulesWidgetsReducer } from 'widgets/myPortfolioCapsules/store/reducer';
import { myPortfolioCapsulesDataReducer } from 'widgets/myPortfolioCapsules/store/data/reducer';
import { reducer as HistoricalPercentageChangeCapsulesReducer } from 'widgets/historicalPercentageChange/capsule/store';
import { reducer as HistoricalPercentageChangeWidgetsReducer } from 'widgets/historicalPercentageChange/widget/store';
import { venueTypeCapsuleWidgetsReducer } from 'widgets/venueAnalysis/capsules/venueTypeCapsule/store/reducer';
import { venueExecCapsuleWidgetsReducer } from 'widgets/venueAnalysis/capsules/venueExecCapsule/store/reducer';
import { corporateActionsDataReducer } from 'widgets/corpActions/store/data/reducer';
import { corporateActionsWidgetsReducer } from 'widgets/corpActions/widget/store/reducer';
import { symbolInfoCapsuleWidgetsReducer } from 'widgets/symbolInfo/capsule/store/reducer';
import { corporateActionsCapsuleWidgetsReducer } from 'widgets/corpActions/capsule/store/reducer';
import { outliersWidgetsReducer } from 'widgets/outliers/store/reducer';
import { perfVsIndexCapsuleWidgetsReducer } from 'widgets/perfVsIndexCapsule/store/reducer';
import { plCapsuleWidgetsReducer } from 'widgets/plCapsule/store/reducer';
import { WidgetType } from 'widgets/WidgetType';
import {
    predictedCloseVolumeCapsuleReducer,
    predictedVolumeCapsuleReducer,
} from 'widgets/liquiditySummary/predictedVolumeCapsule/state/reducer';
import { orderFillsCapsuleWidgetsReducer } from 'widgets/venueAnalysis/capsules/orderFillsCapsulesShared/store/reducer';
import { widgetWithTabTitleWidgetsReducer } from 'widgets/tabTitle/reducer';
import { estimatedTradeCostWidgetsReducer } from 'widgets/estimatedTradeCost/widget/store/reducer';
import darkVenueHeatmapDataReducer from 'widgets/darkVenueHeatmap/store/data/dataSlice';
import estimatedTradeCostDataReducer from 'widgets/estimatedTradeCost/store/data/dataSlice';
import { estimatedTradeCostComparisonWidgetsReducer } from 'widgets/estimatedTradeCost/widget-comparison/store/reducer';
import { orderLiquidityCapsuleWidgetsReducer } from 'widgets/venueAnalysis/capsules/orderLiquidityCapsule/store/reducer';
import { darkVenueHeatmapWidgetsReducer } from 'widgets/darkVenueHeatmap/store/reducer';
import { volumeCurveWidgetsReducer } from 'widgets/volumeCurve/store/reducer';
import { orderVolumeProfileWidgetsReducer } from 'widgets/orderVolumeProfile/store/reducer';
import { indexEventsWidgetsReducer } from 'widgets/indexEvents/store/reducer';
import indexEventsDataReducer from 'widgets/indexEvents/store/data/dataSlice';

const widgetInstancesReducer = combineReducers<WidgetInstancesState>({
    blotter: blotterWidgetsReducer,
    candlestickTile: candlestickTileWidgetsReducer,
    corporateActions: corporateActionsWidgetsReducer,
    corporateActionsCapsule: corporateActionsCapsuleWidgetsReducer,
    [WidgetType.DARK_VENUE_HEATMAP]: darkVenueHeatmapWidgetsReducer,
    [WidgetType.EST_TRADE_COST_WIDGET]: estimatedTradeCostWidgetsReducer,
    [WidgetType.EST_TRADE_COST_COMPARISON_WIDGET]:
        estimatedTradeCostComparisonWidgetsReducer,
    expertiseMake: expertiseMakeWidgetsReducer,
    expertiseNighthawk: expertiseNighthawkWidgetsReducer,
    [WidgetType.EXPERTISE_DNA]: expertiseDnaWidgetsReducer,
    fillsGraph: fillsGraphWidgetsReducer,
    intradayProfiles: intraDayProfileWidgetsReducer,
    [WidgetType.HistoricalPercentageChangeCapsule]:
        HistoricalPercentageChangeCapsulesReducer,
    [WidgetType.HistoricalPercentageChangeWidget]:
        HistoricalPercentageChangeWidgetsReducer,
    [WidgetType.INDEX_EVENTS]: indexEventsWidgetsReducer,
    liquiditySummary: liquiditySummaryWidgetsReducer,
    myPortfolioCapsules: myPortfolioCapsulesWidgetsReducer,
    obvTile: obvTileWidgetsReducer,
    openSubmissions: openSubmissionsWidgetsReducer,
    [WidgetType.OPEN_SUBMISSIONS_CAPSULE]: openSubmissionsCapsuleWidgetsReducer,
    [WidgetType.ORDER_VOLUME_PROFILE]: orderVolumeProfileWidgetsReducer,
    orderFillsByVenueCapsule: orderFillsCapsuleWidgetsReducer(
        WidgetType.ORDER_FILLS_BY_VENUE_CAPSULE
    ),
    orderFillsByVenueTypeCapsule: orderFillsCapsuleWidgetsReducer(
        WidgetType.ORDER_FILLS_BY_VENUE_TYPE_CAPSULE
    ),
    orderLiquidityCapsule: orderLiquidityCapsuleWidgetsReducer,
    outliersWidget: outliersWidgetsReducer,
    perfVsIndexCapsule: perfVsIndexCapsuleWidgetsReducer,
    plCapsule: plCapsuleWidgetsReducer,
    portfolioPl: portfolioPlWidgetsReducer,
    portfolioReturns: portfolioReturnsWidgetsReducer,
    portfolioSummary: portfolioSummaryReducer,
    predictedVsActualTile: volumeCapsuleReducer,
    predictedCloseVolumeCapsule: predictedCloseVolumeCapsuleReducer,
    predictedVolumeCapsule: predictedVolumeCapsuleReducer,
    pfeTile: pfeTileReducer,
    rsiTile: rsiTileReducer,
    strategyParameters: strategyParametersWidgetsReducer,
    supportVisualisation: widgetWithTabTitleWidgetsReducer(
        WidgetType.SUPPORT_VISUALISATION
    ),
    symbolInfo: widgetWithTabTitleWidgetsReducer(WidgetType.SYMBOL_INFO),
    symbolInfoCapsule: symbolInfoCapsuleWidgetsReducer,
    symbolTile: symbolTileWidgetInstancesReducer,
    [WidgetType.TRADE_OPTIMIZER]: widgetWithTabTitleWidgetsReducer(
        WidgetType.TRADE_OPTIMIZER
    ),
    tiles: tilesWidgetsReducer,
    unknown: unknownWidgetsReducer,
    venueAnalysis: venueAnalysisWidgetsReducer,
    venueExecCapsule: venueExecCapsuleWidgetsReducer,
    venueTypeCapsule: venueTypeCapsuleWidgetsReducer,
    [WidgetType.VOLUME_CURVE]: volumeCurveWidgetsReducer,
});

export const widgetsDataReducer = combineReducers<WidgetsDataState>({
    corporateActions: corporateActionsDataReducer,
    darkVenueHeatmap: darkVenueHeatmapDataReducer,
    estimatedTradeCost: estimatedTradeCostDataReducer,
    indexEvents: indexEventsDataReducer,
    portfolio: portfolioSummaryDataReducer,
    myPortfolioCapsulesData: myPortfolioCapsulesDataReducer,
});

export const widgetsReducer = combineReducers<WidgetsState>({
    data: widgetsDataReducer,
    instances: widgetInstancesReducer,
    shared: widgetSharedStateReducer,
    sharedConfig: widgetsSharedConfigReducer,
});
